import React, { useMemo, useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  Camera,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Database,
  FileText,
  Home,
  Mail,
  MapPin,
  Menu,
  Search,
  Settings2,
  ShieldCheck,
  UserRound,
  Moon,
  Sun,
  RotateCcw,
  Smartphone,
  Layers,
  Info,
  Sliders,
  X,
  Wifi,
  Send,
  Trash2,
  FileSpreadsheet
} from 'lucide-react';
import './styles.css';

type Tab = 'home' | 'form' | 'records' | 'alerts';
type Status = 'New' | 'Assigned' | 'In Progress' | 'Review';
type Priority = 'Normal' | 'High' | 'Urgent';

type RecordItem = {
  id: string;
  title: string;
  site: string;
  area: string;
  owner: string;
  department: string;
  status: Status;
  priority: Priority;
  due: string;
  notes: string;
  gps?: string;
  photo?: { url: string; name: string; size: string };
  submittedAt: string;
};

const initialRecordItems: RecordItem[] = [
  {
    id: 'OPS-1048',
    title: 'Safety check',
    site: 'North Yard',
    area: 'Loading Bay 2',
    owner: 'Maya Lee',
    department: 'Operations',
    status: 'In Progress',
    priority: 'High',
    due: 'Today',
    notes: 'Completed morning hazard safety sweep. Clear pathways check.',
    gps: '37.7749° N, 122.4194° W',
    submittedAt: '2026-05-20 09:12'
  },
  {
    id: 'OPS-1047',
    title: 'Equipment issue',
    site: 'Dock 3',
    area: 'Hydraulic Lift B',
    owner: 'Carlos Rivera',
    department: 'Maintenance',
    status: 'Assigned',
    priority: 'Urgent',
    due: 'Today',
    notes: 'Fluid pressure leak detected on main ramp valve. Restricting operations.',
    gps: '37.7782° N, 122.4111° W',
    submittedAt: '2026-05-20 10:05'
  },
  {
    id: 'OPS-1046',
    title: 'Inventory count',
    site: 'Warehouse B',
    area: 'Aisle 12 Shelf C',
    owner: 'Tina Morris',
    department: 'Quality',
    status: 'Review',
    priority: 'Normal',
    due: 'Tomorrow',
    notes: 'Bi-weekly verification of retail boxes. Discrepancy logged.',
    gps: '37.7699° N, 122.4468° W',
    submittedAt: '2026-05-19 16:30'
  },
  {
    id: 'OPS-1045',
    title: 'Client visit',
    site: 'Front Office',
    area: 'Conference Room 1',
    owner: 'Sam Patel',
    department: 'Client Service',
    status: 'New',
    priority: 'Normal',
    due: 'Tomorrow',
    notes: 'Welcome briefing for representatives of Logistics Corp.',
    gps: '37.7550° N, 122.4350° W',
    submittedAt: '2026-05-19 14:02'
  }
];

const sections = [
  'Request Details',
  'Location Data',
  'Details & Photo',
  'Field Assignment',
  'Scheduling',
  'Airtable Review'
];

// Synth audio generation to give native haptic clicks / alerts without remote file dependecies
function playSound(type: 'click' | 'success' | 'alert', enabled: boolean) {
  if (!enabled) return;
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === 'click') {
      osc.frequency.setValueAtTime(850, ctx.currentTime);
      gain.gain.setValueAtTime(0.04, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
      osc.start();
      osc.stop(ctx.currentTime + 0.05);
    } else if (type === 'success') {
      osc.frequency.setValueAtTime(580, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
      osc.start();
      osc.stop(ctx.currentTime + 0.22);
    } else if (type === 'alert') {
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(220, ctx.currentTime);
      osc.frequency.setValueAtTime(330, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.06, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      osc.start();
      osc.stop(ctx.currentTime + 0.2);
    }
  } catch (e) {
    // Ignore audio context blocks
  }
}

// Custom select trigger button which will open our bottom sheet list
interface CustomSelectProps {
  label: string;
  value: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

function CustomSelect({ label, value, icon, onClick }: CustomSelectProps) {
  return (
    <div className="input-block">
      <span>{label}</span>
      <button type="button" className="custom-select-trigger" onClick={onClick}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {icon}
          {value}
        </div>
        <ChevronRight size={16} style={{ transform: 'rotate(90deg)', opacity: 0.5 }} />
      </button>
    </div>
  );
}

export default function App() {
  // Global States
  const [tab, setTab] = useState<Tab>('home');
  const [records, setRecords] = useState<RecordItem[]>(initialRecordItems);
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [soundEnabled, setSoundEnabled] = useState<boolean>(true);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  
  // Custom Toasts state
  const [toasts, setToasts] = useState<{ id: string; title: string; desc: string; type?: 'urgent' | 'normal' }[]>([]);

  // Form State
  const [formStep, setFormStep] = useState(0);
  const [formRequestType, setFormRequestType] = useState('Safety check');
  const [formPriority, setFormPriority] = useState<Priority>('High');
  const [formNotes, setFormNotes] = useState('');
  const [formSite, setFormSite] = useState('North Yard');
  const [formArea, setFormArea] = useState('Loading Bay 2');
  const [formGps, setFormGps] = useState<string>('');
  const [formGpsScanning, setFormGpsScanning] = useState(false);
  const [formPhoto, setFormPhoto] = useState<{ url: string; name: string; size: string } | undefined>(undefined);
  const [formPhotoUploading, setFormPhotoUploading] = useState(false);
  const [formStaff, setFormStaff] = useState('Maya Lee');
  const [formDept, setFormDept] = useState('Operations');
  const [formDue, setFormDue] = useState('Today');
  const [formStatus, setFormStatus] = useState<Status>('In Progress');
  const [formFollowUp, setFormFollowUp] = useState('Yes');

  // Interactive Custom Picker Dialog Config
  const [pickerConfig, setPickerConfig] = useState<{
    isOpen: boolean;
    label: string;
    options: string[];
    value: string;
    onSelect: (val: string) => void;
  } | null>(null);

  // Spreadsheet state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState<string>('All');
  const [filterStatus, setFilterStatus] = useState<string>('All');

  // Trigger Toast Notification Helper
  const triggerToast = (title: string, desc: string, type: 'urgent' | 'normal' = 'normal') => {
    const id = Math.random().toString();
    setToasts((prev) => [...prev, { id, title, desc, type }]);
    playSound(type === 'urgent' ? 'alert' : 'success', soundEnabled);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4500);
  };

  // Keyboard navigation support and physical sound on click
  const handleNavClick = (nextTab: Tab) => {
    playSound('click', soundEnabled);
    setTab(nextTab);
  };

  const handleResetData = () => {
    playSound('alert', soundEnabled);
    setRecords(initialRecordItems);
    triggerToast('Base Reset', 'Simulated Airtable tables restored to factory states.', 'urgent');
  };

  // Coordinates Simulator Trigger
  const handleSimulateGPS = () => {
    playSound('click', soundEnabled);
    setFormGpsScanning(true);
    setTimeout(() => {
      const lat = (37.7 + Math.random() * 0.1).toFixed(4);
      const lng = (-122.4 - Math.random() * 0.1).toFixed(4);
      setFormGps(`${lat}° N, ${lng}° W`);
      setFormGpsScanning(false);
      triggerToast('GPS Locked', `Acquired device location accuracy to +/- 3 meters.`, 'normal');
    }, 1800);
  };

  // Photos upload simulator
  const handleSimulatePhoto = () => {
    playSound('click', soundEnabled);
    setFormPhotoUploading(true);
    setTimeout(() => {
      let url = 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?q=80&w=300&auto=format&fit=crop';
      let name = 'safetypost_dock.jpg';
      if (formRequestType === 'Equipment issue') {
        url = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=300&auto=format&fit=crop';
        name = 'equipment_valve_fail.jpg';
      } else if (formRequestType === 'Inventory count') {
        url = 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=300&auto=format&fit=crop';
        name = 'warehouse_pallet.jpg';
      }
      setFormPhoto({ url, name, size: '2.4 MB' });
      setFormPhotoUploading(false);
      triggerToast('Photo Linked', 'Image verified and uploaded to Airtable attachments array.', 'normal');
    }, 1500);
  };

  // Airtable Form Submission
  const handleSubmitForm = () => {
    // Generate new Airtable style ID
    const nextOpsNumber = 1045 + records.length;
    const newRecord: RecordItem = {
      id: `OPS-${nextOpsNumber}`,
      title: formRequestType,
      site: formSite,
      area: formArea,
      owner: formStaff,
      department: formDept,
      status: formStatus,
      priority: formPriority,
      due: formDue,
      notes: formNotes || `${formRequestType} checked on site at ${formArea}.`,
      gps: formGps || undefined,
      photo: formPhoto,
      submittedAt: new Date().toISOString().replace('T', ' ').substring(0, 16)
    };

    setRecords([newRecord, ...records]);
    
    // Automation Alerts Triggers simulation
    setTimeout(() => {
      triggerToast(
        'Airtable Sync Done', 
        `New record successfully saved to Submissions database (${newRecord.id}).`, 
        'normal'
      );
    }, 500);

    setTimeout(() => {
      triggerToast(
        'Staff Email Dispatched', 
        `Notification sent to ${formStaff} with base record redirection links.`, 
        'normal'
      );
    }, 1800);

    if (formPriority === 'High' || formPriority === 'Urgent') {
      setTimeout(() => {
        triggerToast(
          'URGENT Alert Run', 
          'Supervisor and Operations Lead notified immediately via High-Priority SMS Rule.', 
          'urgent'
        );
      }, 3000);
    }

    // Reset Form
    setFormStep(0);
    setFormNotes('');
    setFormGps('');
    setFormPhoto(undefined);
    playSound('success', soundEnabled);
    
    // Route to spreadsheet database screen to see it live!
    setTab('records');
  };

  // Helper selectors
  const totalUrgent = useMemo(() => records.filter(r => r.priority === 'Urgent').length, [records]);
  const totalNeedReview = useMemo(() => records.filter(r => r.status === 'Review').length, [records]);

  // Filtering records for spreadsheet view
  const filteredRecords = useMemo(() => {
    return records.filter((r) => {
      const matchSearch = 
        r.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.site.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.owner.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchPriority = filterPriority === 'All' || r.priority === filterPriority;
      const matchStatus = filterStatus === 'All' || r.status === filterStatus;
      
      return matchSearch && matchPriority && matchStatus;
    });
  }, [records, searchQuery, filterPriority, filterStatus]);

  const activeRecordItem = useMemo(() => {
    return records.find((r) => r.id === selectedRecordId) || null;
  }, [records, selectedRecordId]);

  return (
    <div className={`desktop-wrapper ${theme === 'light' ? 'light-theme' : ''}`}>
      
      {/* Control Dashboard Deck (Encasing physical simulator layout) */}
      <aside className="control-panel">
        <div>
          <h2><Sliders size={20} /> Base Control</h2>
          <p style={{ marginTop: '0.4rem' }}>
            Interactive control deck simulating developer configurations and Airtable system schema states.
          </p>
        </div>

        <div className="control-btn-group">
          <button 
            className="panel-btn" 
            onClick={() => {
              playSound('click', soundEnabled);
              setTheme(theme === 'dark' ? 'light' : 'dark');
            }}
          >
            <span>Appearance theme</span>
            {theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />}
          </button>

          <button 
            className="panel-btn" 
            onClick={() => {
              playSound('click', soundEnabled);
              setSoundEnabled(!soundEnabled);
            }}
          >
            <span>System audio feeds</span>
            <span style={{ fontSize: '0.8rem', fontWeight: 800, color: soundEnabled ? 'var(--brand-light)' : 'var(--text-muted)' }}>
              {soundEnabled ? 'ACTIVE' : 'MUTED'}
            </span>
          </button>

          <button className="panel-btn" onClick={handleResetData}>
            <span>Reset simulated bases</span>
            <RotateCcw size={18} style={{ color: 'var(--color-urgent)' }} />
          </button>
        </div>

        <div className="tech-specs">
          <div className="spec-row">
            <span className="spec-label">Airtable API Sync</span>
            <span className="spec-val" style={{ color: 'var(--brand-light)' }}>Connected</span>
          </div>
          <div className="spec-row">
            <span className="spec-label">Local Memory DB</span>
            <span className="spec-val">{records.length} records</span>
          </div>
          <div className="spec-row">
            <span className="spec-label">Automations Engine</span>
            <span className="spec-val" style={{ color: 'var(--accent)' }}>3 active rules</span>
          </div>
          <div className="spec-row">
            <span className="spec-label">Latency ping</span>
            <span className="spec-val">12 ms</span>
          </div>
        </div>

        <div style={{ marginTop: 'auto', background: 'rgba(255,255,255,0.02)', padding: '1rem', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
            <Info size={16} style={{ color: 'var(--brand-light)' }} />
            <strong style={{ fontSize: '0.8rem', fontWeight: 800 }}>Field Simulator Mode</strong>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
            Any entry submitted in the <strong>New Record</strong> form wizard will automatically sync into the live <strong>Records Spreadsheet</strong>.
          </p>
        </div>
      </aside>

      {/* Main simulated phone container */}
      <section className="mobile-app">
        <div className="app-shell">
          
          {/* Hardware Notch */}
          <div className="app-notch" />

          {/* Toast Notification Deck */}
          <div className="toast-container">
            {toasts.map((t) => (
              <div key={t.id} className={`toast-bubble ${t.type === 'urgent' ? 'urgent' : ''}`}>
                {t.type === 'urgent' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
                <div className="toast-text">
                  <strong>{t.title}</strong>
                  <span>{t.desc}</span>
                </div>
              </div>
            ))}
          </div>

          {/* SCREEN 1: Home Dashboard Screen */}
          {tab === 'home' && (
            <div className="screen-body">
              <header className="topbar">
                <button type="button" aria-label="Menu" onClick={() => playSound('click', soundEnabled)}><Menu size={18} /></button>
                <div className="topbar-center">
                  <span>Operations</span>
                  <strong>Dashboard</strong>
                </div>
                <button type="button" aria-label="Notifications" onClick={() => playSound('click', soundEnabled)}>
                  <Bell size={18} />
                  {totalUrgent > 0 && <i />}
                </button>
              </header>

              <section className="today-card">
                <div>
                  <span>Daily Summary</span>
                  <strong>{records.length} Submissions</strong>
                  <p style={{ marginTop: '6px' }}>
                    {totalNeedReview} forms await QA verification, {totalUrgent} urgent triggers active.
                  </p>
                </div>
                <button onClick={() => handleNavClick('form')} type="button">
                  New Operational Form
                </button>
              </section>

              <div className="quick-grid">
                <div className="quick-card">
                  <ClipboardList size={18} />
                  <strong>6</strong>
                  <span>Form Stages</span>
                </div>
                <div className="quick-card">
                  <AlertTriangle size={18} style={{ color: totalUrgent > 0 ? 'var(--color-urgent)' : 'var(--brand)' }} />
                  <strong style={{ color: totalUrgent > 0 ? 'var(--color-urgent)' : 'inherit' }}>{totalUrgent}</strong>
                  <span>Urgent Triggers</span>
                </div>
                <div className="quick-card">
                  <Mail size={18} />
                  <strong>3</strong>
                  <span>Email Pipelines</span>
                </div>
                <div className="quick-card">
                  <Database size={18} />
                  <strong>{records.length}</strong>
                  <span>Records Logs</span>
                </div>
              </div>

              <div className="section-header" style={{ marginTop: '0.5rem' }}>
                <strong>Recent Activities</strong>
                <button type="button" onClick={() => handleNavClick('records')}>View all</button>
              </div>

              <section className="card-list">
                {records.slice(0, 3).map((item) => (
                  <article key={item.id} className="record-row" onClick={() => { playSound('click', soundEnabled); setSelectedRecordId(item.id); }}>
                    <div className={`priority-bar ${item.priority.toLowerCase()}`} />
                    <div className="record-info">
                      <strong>{item.title}</strong>
                      <span>{item.id} · {item.site}</span>
                    </div>
                    <div className="record-status-col">
                      <span className={`status-pill ${item.status.toLowerCase().replace(' ', '-')}`}>{item.status}</span>
                      <small>{item.due}</small>
                    </div>
                  </article>
                ))}
              </section>
            </div>
          )}

          {/* SCREEN 2: Multi-step Form Wizard */}
          {tab === 'form' && (
            <div className="screen-body">
              <header className="topbar">
                <button type="button" onClick={() => { playSound('click', soundEnabled); setFormStep(0); setTab('home'); }}>
                  <ChevronLeft size={20} />
                </button>
                <div className="topbar-center">
                  <span>Field Entry</span>
                  <strong>Form Wizard</strong>
                </div>
                <div style={{ width: '42px' }} />
              </header>

              <section className="step-wizard">
                <div className="step-wizard-header">
                  <button 
                    type="button" 
                    className="wizard-nav-btn" 
                    onClick={() => { playSound('click', soundEnabled); setFormStep(Math.max(0, formStep - 1)); }}
                    disabled={formStep === 0}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <div className="step-wizard-info">
                    <span>Section {formStep + 1} of 6</span>
                    <strong>{sections[formStep]}</strong>
                  </div>
                  <button 
                    type="button" 
                    className="wizard-nav-btn" 
                    onClick={() => { playSound('click', soundEnabled); setFormStep(Math.min(sections.length - 1, formStep + 1)); }}
                    disabled={formStep === 5}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
                <div className="progress-track">
                  <div className="progress-fill" style={{ width: `${((formStep + 1) / sections.length) * 100}%` }} />
                </div>
              </section>

              {/* Form Step 1: Request Details */}
              {formStep === 0 && (
                <section className="form-card">
                  <CustomSelect 
                    label="Request type" 
                    value={formRequestType} 
                    icon={<FileText size={18} />}
                    onClick={() => setPickerConfig({
                      isOpen: true,
                      label: 'Request type',
                      options: ['Safety check', 'Equipment issue', 'Inventory count', 'Client visit'],
                      value: formRequestType,
                      onSelect: (val) => setFormRequestType(val)
                    })}
                  />

                  <div className="input-block">
                    <span>Priority classification</span>
                    <div className="segmented-control">
                      {(['Normal', 'High', 'Urgent'] as Priority[]).map((p) => (
                        <button
                          key={p}
                          type="button"
                          className={`segmented-option ${formPriority === p ? 'active ' + p.toLowerCase() : ''}`}
                          onClick={() => { playSound('click', soundEnabled); setFormPriority(p); }}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="input-block">
                    <span>Field entry notes</span>
                    <textarea 
                      className="text-area-input" 
                      placeholder="Enter detailed observations here..." 
                      value={formNotes}
                      onChange={(e) => setFormNotes(e.target.value)}
                    />
                  </div>
                </section>
              )}

              {/* Form Step 2: Location Data */}
              {formStep === 1 && (
                <section className="form-card">
                  <CustomSelect 
                    label="Assigned Site" 
                    value={formSite} 
                    icon={<MapPin size={18} />}
                    onClick={() => setPickerConfig({
                      isOpen: true,
                      label: 'Assigned Site',
                      options: ['North Yard', 'Dock 3', 'Warehouse B', 'Front Office'],
                      value: formSite,
                      onSelect: (val) => setFormSite(val)
                    })}
                  />

                  <div className="input-block">
                    <span>Specific Area Location</span>
                    <input 
                      type="text" 
                      className="fake-input" 
                      style={{ borderStyle: 'solid', outline: 'none' }}
                      value={formArea}
                      onChange={(e) => setFormArea(e.target.value)}
                    />
                  </div>

                  <div className="input-block">
                    <span>Satellite GPS Coordinates</span>
                    <div className="gps-scanner-widget">
                      {formGpsScanning ? (
                        <div className="gps-map-container">
                          <div className="gps-radar-sweeper" />
                          <div className="gps-radar-wave" />
                          <div className="gps-radar-wave w2" />
                          <div className="gps-radar-wave w3" />
                          <span style={{ position: 'relative', zIndex: 10, fontSize: '0.75rem', fontWeight: 800, color: 'var(--brand-light)' }}>
                            Pinging satellites...
                          </span>
                        </div>
                      ) : formGps ? (
                        <div className="gps-map-container">
                          <div className="gps-ping-point" />
                          <div className="gps-coordinates-overlay">
                            <strong>COORDS ACCREDITED</strong>
                            <span>{formGps}</span>
                          </div>
                        </div>
                      ) : (
                        <button type="button" className="gps-action-btn" onClick={handleSimulateGPS}>
                          <MapPin size={16} /> Get phone GPS coordinates
                        </button>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {/* Form Step 3: Details & Attachments (Conditional rendering) */}
              {formStep === 2 && (
                <section className="form-card">
                  {formRequestType === 'Safety check' && (
                    <div className="conditional-alert safety">
                      <ShieldCheck size={20} />
                      <div>
                        <strong style={{ fontSize: '0.85rem' }}>Safety Rules Engaged</strong>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginTop: '2px' }}>
                          Requires hazard severity documentation and photo proof verification.
                        </span>
                      </div>
                    </div>
                  )}

                  {formRequestType === 'Equipment issue' && (
                    <div className="conditional-alert equipment">
                      <Settings2 size={20} />
                      <div>
                        <strong style={{ fontSize: '0.85rem' }}>Asset Logistics Engaged</strong>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginTop: '2px' }}>
                          Generates urgent service order and repairs team task allocations.
                        </span>
                      </div>
                    </div>
                  )}

                  {(formPriority === 'High' || formPriority === 'Urgent') && (
                    <div className="conditional-alert urgent">
                      <AlertTriangle size={20} />
                      <div>
                        <strong style={{ fontSize: '0.85rem' }}>Priority Notification Escalation</strong>
                        <span style={{ fontSize: '0.75rem', color: '#f8a5a5', display: 'block', marginTop: '2px' }}>
                          Operations Lead and General Manager will receive priority cellular pings on submit.
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="input-block">
                    <span>Reference Attachment Upload</span>
                    {formPhotoUploading ? (
                      <div className="photo-uploader" style={{ background: 'rgba(255, 255, 255, 0.01)', borderStyle: 'solid' }}>
                        <Camera size={24} style={{ animation: 'pulse 1.2s infinite ease-in-out' }} />
                        <span>Compiling imagery data...</span>
                      </div>
                    ) : formPhoto ? (
                      <div className="photo-preview-box">
                        <img src={formPhoto.url} alt="Airtable Attachment" className="photo-preview-img" />
                        <button type="button" className="photo-remove-btn" onClick={() => { playSound('alert', soundEnabled); setFormPhoto(undefined); }}>
                          <Trash2 size={16} />
                        </button>
                        <div style={{ padding: '8px 12px', fontSize: '0.75rem', background: 'rgba(9,13,12,0.9)', display: 'flex', justifyContent: 'space-between' }}>
                          <strong>{formPhoto.name}</strong>
                          <span style={{ color: 'var(--text-muted)' }}>{formPhoto.size}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="photo-uploader" onClick={handleSimulatePhoto}>
                        <Camera size={24} />
                        <span>Tap to snap or upload field photo</span>
                        <small>PNG, JPG up to 10MB (Auto-compressed)</small>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* Form Step 4: Field Assignment */}
              {formStep === 3 && (
                <section className="form-card">
                  <CustomSelect 
                    label="Assigned Lead Staff" 
                    value={formStaff} 
                    icon={<UserRound size={18} />}
                    onClick={() => setPickerConfig({
                      isOpen: true,
                      label: 'Assigned Lead Staff',
                      options: ['Maya Lee', 'Carlos Rivera', 'Tina Morris', 'Sam Patel'],
                      value: formStaff,
                      onSelect: (val) => setFormStaff(val)
                    })}
                  />

                  <CustomSelect 
                    label="Involved Department" 
                    value={formDept} 
                    icon={<Layers size={18} />}
                    onClick={() => setPickerConfig({
                      isOpen: true,
                      label: 'Involved Department',
                      options: ['Operations', 'Maintenance', 'Quality', 'Client Service'],
                      value: formDept,
                      onSelect: (val) => setFormDept(val)
                    })}
                  />

                  <div className="input-block">
                    <span>Backup Base Contact</span>
                    <input 
                      type="text" 
                      className="fake-input" 
                      style={{ borderStyle: 'solid', outline: 'none' }}
                      value="Ops Supervisor (Auto)"
                      readOnly
                    />
                  </div>
                </section>
              )}

              {/* Form Step 5: Scheduling */}
              {formStep === 4 && (
                <section className="form-card">
                  <CustomSelect 
                    label="Schedule Deadline" 
                    value={formDue} 
                    icon={<CalendarClock size={18} />}
                    onClick={() => setPickerConfig({
                      isOpen: true,
                      label: 'Schedule Deadline',
                      options: ['Today', 'Tomorrow', 'End of week', 'Next Week'],
                      value: formDue,
                      onSelect: (val) => setFormDue(val)
                    })}
                  />

                  <CustomSelect 
                    label="Operational Status" 
                    value={formStatus} 
                    icon={<Sliders size={18} />}
                    onClick={() => setPickerConfig({
                      isOpen: true,
                      label: 'Operational Status',
                      options: ['New', 'Assigned', 'In Progress', 'Review'],
                      value: formStatus,
                      onSelect: (val) => setFormStatus(val as Status)
                    })}
                  />

                  <div className="input-block">
                    <span>Supervisor Follow-up Needed?</span>
                    <div className="segmented-control">
                      {['No', 'Yes'].map((val) => (
                        <button
                          key={val}
                          type="button"
                          className={`segmented-option ${formFollowUp === val ? 'active' : ''}`}
                          onClick={() => { playSound('click', soundEnabled); setFormFollowUp(val); }}
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* Form Step 6: Airtable Integration Review */}
              {formStep === 5 && (
                <section className="form-card">
                  <div className="review-checklist">
                    <div className="review-item">
                      <Check size={16} />
                      <div>
                        <strong style={{ fontSize: '0.85rem' }}>Schema Alignment Passed</strong>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'block' }}>
                          Fields match table Airtable configurations.
                        </span>
                      </div>
                    </div>

                    <div className="review-item">
                      <Check size={16} />
                      <div>
                        <strong style={{ fontSize: '0.85rem' }}>Metadata Verification</strong>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'block' }}>
                          GPS: {formGps ? 'LOCATED' : 'NOT CAPTURED'} · Photo: {formPhoto ? 'ATTACHED' : 'NONE'}
                        </span>
                      </div>
                    </div>

                    <div className="review-item">
                      <Check size={16} />
                      <div>
                        <strong style={{ fontSize: '0.85rem' }}>Automated Workflows Ready</strong>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'block' }}>
                          Email rule triggers aligned for {formStaff}.
                        </span>
                      </div>
                    </div>
                  </div>

                  <button type="button" className="submit-airtable-btn" onClick={handleSubmitForm}>
                    Submit to Airtable Base
                  </button>
                </section>
              )}
            </div>
          )}

          {/* SCREEN 3: Active Submissions Database Spreadsheet Grid */}
          {tab === 'records' && (
            <div className="screen-body">
              <header className="topbar">
                <button type="button" onClick={() => handleNavClick('home')}><ChevronLeft size={20} /></button>
                <div className="topbar-center">
                  <span>Airtable View</span>
                  <strong>Spreadsheet Grid</strong>
                </div>
                <div style={{ width: '42px' }} />
              </header>

              <div className="spreadsheet-box">
                <div className="search-box">
                  <Search size={16} />
                  <input 
                    type="text" 
                    placeholder="Search ID, site, owner..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button type="button" style={{ border: 'none', background: 'transparent' }} onClick={() => setSearchQuery('')}>
                      <X size={16} style={{ color: 'var(--text-muted)' }} />
                    </button>
                  )}
                </div>

                <div className="filter-pills">
                  {(['All', 'Normal', 'High', 'Urgent'] as string[]).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`filter-pill ${filterPriority === p ? 'active' : ''}`}
                      onClick={() => { playSound('click', soundEnabled); setFilterPriority(p); }}
                    >
                      {p} Priority
                    </button>
                  ))}
                </div>

                <div className="filter-pills" style={{ paddingTop: 0, borderBottom: '1px solid var(--border-color)' }}>
                  {(['All', 'New', 'Assigned', 'In Progress', 'Review'] as string[]).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`filter-pill ${filterStatus === s ? 'active' : ''}`}
                      onClick={() => { playSound('click', soundEnabled); setFilterStatus(s); }}
                    >
                      {s} Status
                    </button>
                  ))}
                </div>

                <div className="grid-table-container">
                  <table className="grid-table">
                    <thead>
                      <tr>
                        <th>Record ID</th>
                        <th>Request Type</th>
                        <th>Site</th>
                        <th>Owner</th>
                        <th>Priority</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecords.length > 0 ? (
                        filteredRecords.map((item) => (
                          <tr key={item.id} onClick={() => { playSound('click', soundEnabled); setSelectedRecordId(item.id); }}>
                            <td className="grid-meta-tag">{item.id}</td>
                            <td className="grid-text-bold">{item.title}</td>
                            <td>{item.site}</td>
                            <td>{item.owner}</td>
                            <td>
                              <span style={{ 
                                color: item.priority === 'Urgent' ? 'var(--color-urgent)' : item.priority === 'High' ? 'var(--color-high)' : 'var(--color-normal)',
                                fontWeight: 700 
                              }}>
                                {item.priority}
                              </span>
                            </td>
                            <td>
                              <span className={`status-pill ${item.status.toLowerCase().replace(' ', '-')}`}>
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                            No synced records match these query filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* SCREEN 4: Alerts & Automation Rules Center */}
          {tab === 'alerts' && (
            <div className="screen-body">
              <header className="topbar">
                <button type="button" onClick={() => handleNavClick('home')}><ChevronLeft size={20} /></button>
                <div className="topbar-center">
                  <span>System Engine</span>
                  <strong>Workflow Rules</strong>
                </div>
                <div style={{ width: '42px' }} />
              </header>

              <section className="alert-flow">
                <h3><Send size={18} style={{ color: 'var(--brand)' }} /> Email Automation Flow</h3>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  Airtable automated mail triggers which trigger dynamically depending on form fields.
                </p>

                <div className="flow-container">
                  {/* Rule 1: High Priority Alert Rule */}
                  <div className={`flow-card-node urgent ${formPriority === 'Urgent' || formPriority === 'High' ? 'active' : ''}`}>
                    <div className="flow-icon">
                      <AlertTriangle size={18} />
                    </div>
                    <div className="flow-node-text">
                      <strong>Urgent Priority Alert Dispatcher</strong>
                      <span>Sends immediate SMS/Email alert to Supervisor & Operations Lead.</span>
                    </div>
                    <span className="flow-trigger-pill">
                      {formPriority === 'Urgent' || formPriority === 'High' ? 'TRIGGERED' : 'STANDBY'}
                    </span>
                  </div>

                  {/* Rule 2: Assigned Staff Rule */}
                  <div className={`flow-card-node active`}>
                    <div className="flow-icon">
                      <Mail size={18} />
                    </div>
                    <div className="flow-node-text">
                      <strong>Task Assignment Dispatcher</strong>
                      <span>Dispatches detailed checklist records directly to {formStaff || 'Staff'}.</span>
                    </div>
                    <span className="flow-trigger-pill">ACTIVE</span>
                  </div>

                  {/* Rule 3: Quality Review Trigger */}
                  <div className={`flow-card-node ${formStatus === 'Review' ? 'active' : ''}`}>
                    <div className="flow-icon">
                      <ShieldCheck size={18} />
                    </div>
                    <div className="flow-node-text">
                      <strong>Audit Log Verification Trigger</strong>
                      <span>Emails Quality Assurance board once status resolves to "Review".</span>
                    </div>
                    <span className="flow-trigger-pill">
                      {formStatus === 'Review' ? 'TRIGGERED' : 'STANDBY'}
                    </span>
                  </div>
                </div>
              </section>

              <section className="base-map">
                <h3><Database size={18} /> Connected Tables Mapping</h3>
                <div className="base-map-tables">
                  <div className="base-map-cell"><Database size={14} /> Submissions</div>
                  <div className="base-map-cell"><UserRound size={14} /> Staff Members</div>
                  <div className="base-map-cell"><MapPin size={14} /> Site Warehouses</div>
                  <div className="base-map-cell"><FileSpreadsheet size={14} /> Audit Log History</div>
                </div>
              </section>
            </div>
          )}

          {/* Device Bottom Tab Navigation */}
          <nav className="bottom-nav">
            <button className={`bottom-nav-btn ${tab === 'home' ? 'active' : ''}`} onClick={() => handleNavClick('home')} type="button">
              <Home size={20} />
              <span>Home</span>
            </button>
            <button className={`bottom-nav-btn ${tab === 'form' ? 'active' : ''}`} onClick={() => handleNavClick('form')} type="button">
              <ClipboardList size={20} />
              <span>Form</span>
            </button>
            <button className={`bottom-nav-btn ${tab === 'records' ? 'active' : ''}`} onClick={() => handleNavClick('records')} type="button">
              <FileSpreadsheet size={20} />
              <span>Grid View</span>
            </button>
            <button className={`bottom-nav-btn ${tab === 'alerts' ? 'active' : ''}`} onClick={() => handleNavClick('alerts')} type="button">
              <Bell size={20} />
              <span>Automation</span>
            </button>
          </nav>

          {/* CUSTOM PICKER BOTTOM SHEET OVERLAY DIALOG */}
          <div 
            className={`modal-overlay ${pickerConfig?.isOpen ? 'open' : ''}`} 
            onClick={() => pickerConfig && setPickerConfig({ ...pickerConfig, isOpen: false })}
          >
            <div className="bottom-sheet" onClick={(e) => e.stopPropagation()}>
              <div className="bottom-sheet-header">
                <strong>Select {pickerConfig?.label}</strong>
                <button type="button" onClick={() => pickerConfig && setPickerConfig({ ...pickerConfig, isOpen: false })}>
                  <X size={20} />
                </button>
              </div>

              <div className="select-options-list">
                {pickerConfig?.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`select-option-row ${pickerConfig.value === option ? 'selected' : ''}`}
                    onClick={() => {
                      playSound('click', soundEnabled);
                      pickerConfig.onSelect(option);
                      setPickerConfig({ ...pickerConfig, isOpen: false });
                    }}
                  >
                    <span>{option}</span>
                    {pickerConfig.value === option && <Check size={16} />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* SPREADSHEET ROW DETAIL INSPECTOR DRAWER */}
          <div 
            className={`drawer-backdrop ${selectedRecordId ? 'open' : ''}`}
            onClick={() => setSelectedRecordId(null)}
          >
            <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
              {activeRecordItem && (
                <>
                  <div className="drawer-header">
                    <div>
                      <span className="grid-meta-tag" style={{ display: 'block', fontSize: '0.8rem' }}>
                        {activeRecordItem.id}
                      </span>
                      <strong>{activeRecordItem.title}</strong>
                    </div>
                    <button type="button" onClick={() => setSelectedRecordId(null)}>
                      <X size={22} />
                    </button>
                  </div>

                  <div className="drawer-content">
                    {activeRecordItem.photo && (
                      <div className="drawer-field-row">
                        <label>Attachment Picture</label>
                        <img 
                          src={activeRecordItem.photo.url} 
                          alt="Record File" 
                          style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '12px', marginTop: '6px', border: '1px solid var(--glass-border)' }} 
                        />
                      </div>
                    )}

                    <div className="drawer-field-row">
                      <label>Priority Class</label>
                      <div style={{ 
                        color: activeRecordItem.priority === 'Urgent' ? 'var(--color-urgent)' : activeRecordItem.priority === 'High' ? 'var(--color-high)' : 'var(--color-normal)',
                        fontWeight: 800
                      }}>
                        {activeRecordItem.priority}
                      </div>
                    </div>

                    <div className="drawer-field-row">
                      <label>Operational Status</label>
                      <span className={`status-pill ${activeRecordItem.status.toLowerCase().replace(' ', '-')}`} style={{ alignSelf: 'flex-start', marginTop: '4px' }}>
                        {activeRecordItem.status}
                      </span>
                    </div>

                    <div className="drawer-field-row">
                      <label>Assigned Site & Area</label>
                      <div>{activeRecordItem.site} ({activeRecordItem.area})</div>
                    </div>

                    {activeRecordItem.gps && (
                      <div className="drawer-field-row">
                        <label>GPS Coordinates</label>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--brand-light)' }}>
                          {activeRecordItem.gps}
                        </div>
                      </div>
                    )}

                    <div className="drawer-field-row">
                      <label>Primary Owner</label>
                      <div>{activeRecordItem.owner} ({activeRecordItem.department})</div>
                    </div>

                    <div className="drawer-field-row">
                      <label>Due Date</label>
                      <div>{activeRecordItem.due}</div>
                    </div>

                    <div className="drawer-field-row">
                      <label>Observations Notes</label>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5, background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                        {activeRecordItem.notes}
                      </div>
                    </div>

                    <div className="drawer-field-row" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                      <label>Sync Verification Log</label>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'monospace', display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                        <div>[2026-05-20] Webhook validation: PASSED</div>
                        <div>[2026-05-20] Airtable Base sync status: SUCCESS</div>
                        <div>[2026-05-20] Auto Notification dispatch: COMPLETE</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </section>

    </div>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
